// Where HTTP and keyword MONITORS check from, said once for every page (OpenSpec 121, claims policy).
//
// Two different things have a location count:
//  - the free "Is X down?" checker fans out to 26 places (2 core probes + 24 edge cities). That is live,
//    so its pages say EDGE_LOCATION_COUNT, the edge cities (see `locations.ts`, `down-page.ts`, the tools pages);
//  - MONITORS run from the 2 core locations and, since 2026-11-02, from the 24 added cities, with alerts
//    (OpenSpec 121).
//
// The Ashburn core probe was retired on 2026-11-05 (the owner cancelled its VPS), so the core probes are
// Nuremberg and Hyderabad, and the checker has 26 rows.
//
// Every monitor claim reads this file. `MONITOR_EDGE_LIVE = false` brings back the wording for core locations only.

export const MONITOR_EDGE_LIVE = true

// These three numbers and the city list are checked against `LOCATIONS` in `-claims.test.ts`. They are not
// imported from `locations.ts`, because that file imports `free-plan.ts`, which imports this one.
/** Locations of the free "Is X down?" checker (live). */
export const TOOL_LOCATION_COUNT = 26 // 24 edge cities + 2 core probes; the public count is EDGE_LOCATION_COUNT
export const CORE_LOCATION_COUNT = 2
export const ADDED_CITY_COUNT = TOOL_LOCATION_COUNT - CORE_LOCATION_COUNT
/** The location count that public copy uses (owner decision, 2026-11-02): the edge cities. */
export const EDGE_LOCATION_COUNT = ADDED_CITY_COUNT
/** The core locations that run every monitor. */
export const CORE_CITIES = 'Nuremberg and Hyderabad'

const live = MONITOR_EDGE_LIVE
const pick = <T>(whenLive: T, today: T): T => (live ? whenLive : today)

const sentence = pick(
  `HTTP and keyword monitors check from ${EDGE_LOCATION_COUNT} locations on six continents.`,
  `HTTP and keyword monitors check from ${CORE_CITIES} today. ${ADDED_CITY_COUNT} more cities are rolling out.`,
)
const confirm = pick(
  'Several locations must confirm a failure before you get an alert.',
  'A majority of the locations must confirm a failure before you get an alert.',
)
const status = `Status, 2026-10-12: monitors check from the ${CORE_LOCATION_COUNT} core locations (${CORE_CITIES}) while the ${ADDED_CITY_COUNT} added cities roll out. The free "Is it down?" checker already uses all ${TOOL_LOCATION_COUNT}.`

/** Every wording a page needs. A page imports the string; it never writes the count itself. */
export const MONITOR_LOCATIONS = {
  /** One sentence for FAQs, llms files, notes. */
  sentence,
  /** The rule that keeps one slow city from paging you. */
  confirm,
  /** A short feature bullet, no full stop. */
  bullet: pick(
    `HTTP and keyword checks from ${EDGE_LOCATION_COUNT} locations on 6 continents`,
    `HTTP and keyword checks from ${CORE_CITIES} today, more cities rolling out`,
  ),
  /** Proof strip and live stats. */
  stat: {
    value: pick(String(EDGE_LOCATION_COUNT), String(CORE_LOCATION_COUNT)),
    label: pick(
      'check locations on 6 continents (6 areas on Pro, all on Team)',
      `check locations today, ${ADDED_CITY_COUNT} more cities rolling out`,
    ),
    sub: pick('on six continents', `today, ${ADDED_CITY_COUNT} more rolling out`),
  },
  globe: {
    label: pick(`${EDGE_LOCATION_COUNT} check locations on six continents`, `${CORE_LOCATION_COUNT} check locations today, ${ADDED_CITY_COUNT} more rolling out`),
    confirm: pick('Several locations must confirm before we alert you', 'A majority of locations must confirm before we alert you'),
  },
  /** Home "region checks" list. */
  regionList: {
    right: pick(`6 areas on Pro · all ${EDGE_LOCATION_COUNT} on Team and Business`, `${CORE_LOCATION_COUNT} live today · ${ADDED_CITY_COUNT} rolling out`),
    footer: pick(
      `${EDGE_LOCATION_COUNT} locations in 12 areas on six continents. Several of them must confirm a failure before you are paged.`,
      `${CORE_LOCATION_COUNT} locations run your monitors today. The other ${ADDED_CITY_COUNT} cities are rolling out. A majority must confirm a failure before you are paged.`,
    ),
    /** Small tag after a city that does not run monitors yet; empty when live. */
    tag: pick('', 'rolling out'),
  },
  /** Home story, showcase and consensus picture. */
  story: pick(
    `Paid plans check your site from 6 or 12 areas, up to ${EDGE_LOCATION_COUNT} locations, on six continents and alert only when several locations confirm it is down.`,
    'Paid plans check your site from several locations and alert only when a majority of them confirm it is down.',
  ),
  showcase: {
    title: pick(`${EDGE_LOCATION_COUNT} locations, one verdict`, 'Locations vote, one verdict'),
    desc: pick(
      `Your CDN edge in Frankfurt goes down and your server is fine. Uptrack checks from ${EDGE_LOCATION_COUNT} locations on six continents and alerts only when several of them confirm the failure.`,
      `Your CDN edge in Frankfurt goes down and your server is fine. Uptrack checks from ${CORE_CITIES} today and alerts only when a majority of them confirm the failure.`,
    ),
  },
  consensusPicture: {
    rows: pick(
      [
        { name: 'EU · Nuremberg', ms: '42 ms', up: true },
        { name: 'Asia · Singapore', ms: '188 ms', up: true },
        { name: 'South America · São Paulo', ms: '231 ms', up: true },
        { name: 'Australia · Sydney', ms: 'timeout', up: false },
      ],
      [
        { name: 'EU · Nuremberg', ms: '42 ms', up: true },
        { name: 'Asia · Hyderabad', ms: 'timeout', up: false },
      ],
    ),
    label: pick(
      `Example: Sydney timed out while ${ADDED_CITY_COUNT - 1} of ${ADDED_CITY_COUNT} locations are up, so no alert is sent`,
      'Example: Hyderabad timed out while 1 of 2 locations is up, so no alert is sent',
    ),
  },
  /** Pricing. */
  plan: {
    feature: {
      pro: pick('6 check areas (you pick)', `${CORE_LOCATION_COUNT} check locations today (more cities rolling out)`),
      team: pick(`All ${EDGE_LOCATION_COUNT} check locations`, `${CORE_LOCATION_COUNT} check locations today (more cities rolling out)`),
      business: pick(`All ${EDGE_LOCATION_COUNT} check locations`, `${CORE_LOCATION_COUNT} check locations today (more cities rolling out)`),
    },
    cardValue: { pro: pick('6 areas', String(CORE_LOCATION_COUNT)), team: pick(String(EDGE_LOCATION_COUNT), String(CORE_LOCATION_COUNT)), business: pick(String(EDGE_LOCATION_COUNT), String(CORE_LOCATION_COUNT)) },
    cardLine: {
      pro: pick('You pick 6 of 12 check areas', `Checks from ${CORE_CITIES} today`),
      team: pick(`All ${EDGE_LOCATION_COUNT} check locations on six continents`, `Checks from ${CORE_CITIES} today`),
      business: pick(`All ${EDGE_LOCATION_COUNT} check locations on six continents`, `Checks from ${CORE_CITIES} today`),
    },
    tableCell: { pro: pick('6 areas (you pick)', String(CORE_LOCATION_COUNT)), team: pick(String(EDGE_LOCATION_COUNT), String(CORE_LOCATION_COUNT)), business: pick(String(EDGE_LOCATION_COUNT), String(CORE_LOCATION_COUNT)) },
    /** JSON-LD offer descriptions. */
    schema: { pro: pick('6 check areas', `${CORE_LOCATION_COUNT} check locations today`), team: pick(`${EDGE_LOCATION_COUNT} check locations`, `${CORE_LOCATION_COUNT} check locations today`), business: pick(`${EDGE_LOCATION_COUNT} check locations`, `${CORE_LOCATION_COUNT} check locations today`) },
    tooltip: pick(
      `${EDGE_LOCATION_COUNT} locations on six continents for HTTP and keyword monitors: Pro picks 6 areas, Team and Business use all ${EDGE_LOCATION_COUNT}. Ping, port, DNS and SSL checks run from Germany and India. Several locations must confirm a failure before we alert, so a CDN blip in one city stays quiet. The free plan checks from Nuremberg only.`,
      `${sentence} Ping, port, DNS and SSL checks run from the same ${CORE_LOCATION_COUNT} locations. ${confirm} The free plan checks from Nuremberg only.`,
    ),
    hero: pick(
      `On paid plans, several of ${EDGE_LOCATION_COUNT} locations confirm a failure before we alert you.`,
      'On paid plans, a majority of the locations confirm a failure before we alert you.',
    ),
    /** "Where we check from" section lead. */
    sectionLead: pick(
      `HTTP and keyword monitors run from ${EDGE_LOCATION_COUNT} locations on six continents. Free uses Nuremberg only; Pro picks 6 of the 12 areas; Team and Business use all ${EDGE_LOCATION_COUNT}.`,
      `${sentence} Free uses Nuremberg only. The free "Is it down?" checker already uses all ${TOOL_LOCATION_COUNT}.`,
    ),
    sectionFooter: pick(
      `Ping, TCP, DNS, SSL and domain checks run from the ${CORE_LOCATION_COUNT} core locations: ${CORE_CITIES}. Several locations must confirm a failure before you get an alert.`,
      `Ping, TCP, DNS, SSL and domain checks run from the same ${CORE_LOCATION_COUNT} locations. ${confirm}`,
    ),
    faqWhere: pick(
      `${EDGE_LOCATION_COUNT} locations on six continents: Europe, North and South America, Asia, Australia and Africa. Pro picks 6 areas; Team and Business use all ${EDGE_LOCATION_COUNT}. Ping, port, DNS and SSL checks run from Germany and India.`,
      `${sentence} Ping, port, DNS and SSL checks run from Germany and India.`,
    ),
    /** What Pro adds (used in "Pro adds ..."). */
    proAdds: pick('50 monitors, 6 of 12 check areas, webhooks and 5 status pages', '50 monitors, webhooks and 5 status pages'),
    faqNotIncluded: pick(
      `Pro adds 50 monitors, 6 of 12 check areas, webhooks and 5 status pages, from $3/mo. Team and Business add all ${EDGE_LOCATION_COUNT} locations and more.`,
      'Pro adds 50 monitors, webhooks and 5 status pages, from $3/mo. Team and Business add more monitors and more features.',
    ),
  },
  /** Monitor form and error hints. */
  freeHint: pick('The free plan checks from one location. Pro lets you pick 6 of 12 areas.', 'The free plan checks from one location.'),
  formHint: pick(
    'The free plan checks from one location. Pro lets you pick 6 of 12 check areas on six continents.',
    'The free plan checks from one location.',
  ),
  /** Feature, audience and glossary pages. */
  sre: pick(`Checks run from ${EDGE_LOCATION_COUNT} locations worldwide.`, `Checks run from ${CORE_CITIES} today.`),
  glossary: pick(
    `from up to ${EDGE_LOCATION_COUNT} locations on six continents`,
    `from ${CORE_CITIES} today, with more cities rolling out`,
  ),
  /** Comparison pages. */
  compare: {
    cell: pick(`${EDGE_LOCATION_COUNT} for HTTP/keyword (6 areas on Pro); ${CORE_LOCATION_COUNT} for ping, TCP, DNS, SSL`, `${CORE_LOCATION_COUNT} today (${CORE_CITIES}); ${ADDED_CITY_COUNT} more rolling out`),
    /** A row where Uptrack beat a competitor on the count of locations. */
    wins: live,
    pingdom: pick(
      `Uptrack checks HTTP and keyword monitors from ${EDGE_LOCATION_COUNT} locations.`,
      `Uptrack checks from ${CORE_LOCATION_COUNT} locations today (${CORE_CITIES}).`,
    ),
    site24x7: pick(
      `Uptrack checks from ${EDGE_LOCATION_COUNT} locations (6 areas on Pro), and several must confirm before alerting.`,
      `Uptrack checks from ${CORE_LOCATION_COUNT} locations today, and a majority must confirm before alerting.`,
    ),
  },
  /** The "cons" line on the alternatives pages. */
  alternativesCon: pick(
    `Ping, TCP, DNS and SSL checks run from ${CORE_LOCATION_COUNT} locations (HTTP and keyword from ${EDGE_LOCATION_COUNT})`,
    `Checks run from ${CORE_LOCATION_COUNT} locations today; ${ADDED_CITY_COUNT} more cities are rolling out`,
  ),
  /** A status note for dated posts that said 22 (blog, changelog). */
  status: pick(`Update, November 2026: HTTP and keyword monitors now check from ${EDGE_LOCATION_COUNT} locations in 12 areas, and Pro picks 6 areas.`, status),
  blogDescription: pick(
    `HTTP and keyword monitors now run from ${EDGE_LOCATION_COUNT} locations on six continents. How cities take turns, confirm failures and handle blocks.`,
    `How ${EDGE_LOCATION_COUNT} cities can take turns, confirm failures and handle blocks. The cities are rolling out: monitors check from ${CORE_CITIES} today.`,
  ),
  archived: pick(
    `HTTP and keyword monitors now run from ${EDGE_LOCATION_COUNT} locations on six continents.`,
    `HTTP and keyword monitors check from ${CORE_CITIES} today; more cities are rolling out.`,
  ),
} as const

/** The free checker. Always true while the edge bridge answers it. */
export const TOOL_LOCATIONS_SENTENCE = `The free "Is it down?" checker checks from ${EDGE_LOCATION_COUNT} locations and ${CORE_LOCATION_COUNT} core probes on six continents.`
